let chartJsLoaderPromise = null;

const ensureChartJSLoaded = () => {
  if (window.Chart) {
    return Promise.resolve(window.Chart);
  }

  if (!chartJsLoaderPromise) {
    chartJsLoaderPromise = import('chart.js/auto')
      .then((mod) => {
        window.Chart = mod.default || mod;
        return window.Chart;
      })
      .catch((error) => {
        chartJsLoaderPromise = null;
        throw error;
      });
  }

  return chartJsLoaderPromise;
};

const deepMerge = (target = {}, source = {}) => {
  const result = { ...target };

  Object.keys(source).forEach((key) => {
    const value = source[key];

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = deepMerge(result[key] || {}, value);
    } else {
      result[key] = value;
    }
  });

  return result;
};

const escapeHtml = (value = '') => value.replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
}[char]));

export const ChartJSHook = {
  async mounted() {
    try {
      this.hideError();
      await ensureChartJSLoaded();
      this.renderChart();
    } catch (error) {
      console.error('[ChartJS Hook] erro ao carregar Chart.js', error);
      this.showError();
    }
  },

  async updated() {
    if (!this.chart) {
      return this.mounted();
    }

    try {
      this.hideError();
      const chartData = JSON.parse(this.el.dataset.chartData || '{}');
      const chartOptions = JSON.parse(this.el.dataset.chartOptions || '{}');

      this.chart.data = chartData;
      this.chart.options = deepMerge(this.chart.options || {}, chartOptions);
      this.chart.update('none');
    } catch (error) {
      console.error('[ChartJS Hook] erro ao atualizar gráfico', error);
      this.showError();
    }
  },

  destroyed() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },

  renderChart() {
    try {
      const chartType = this.el.dataset.chartType || 'bar';
      const chartData = JSON.parse(this.el.dataset.chartData || '{}');
      const chartOptions = JSON.parse(this.el.dataset.chartOptions || '{}');

      const canvas = this.el.querySelector('canvas');
      if (!canvas) {
        throw new Error('Canvas não encontrado para o gráfico');
      }

      const ctx = canvas.getContext('2d');

      if (this.chart) {
        this.chart.destroy();
      }

      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 16,
              font: { family: "'Inter', sans-serif", size: 12 },
              color: '#0f172a'
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            borderWidth: 0,
            padding: 10,
            cornerRadius: 8,
            titleFont: { family: "'Inter', sans-serif", size: 12 },
            bodyFont: { family: "'Inter', sans-serif", size: 11 }
          }
        },
        animation: { duration: 700, easing: 'easeOutQuart' }
      };

      if (['line', 'bar', 'area'].includes(chartType)) {
        defaultOptions.scales = {
          x: {
            ticks: { color: '#0f172a', font: { family: "'Inter', sans-serif", size: 11 } },
            grid: { color: 'rgba(15, 23, 42, 0.08)', drawBorder: false }
          },
          y: {
            beginAtZero: true,
            ticks: { color: '#0f172a', font: { family: "'Inter', sans-serif", size: 11 } },
            grid: { color: 'rgba(15, 23, 42, 0.08)', drawBorder: false }
          }
        };
      }

      const finalOptions = deepMerge(defaultOptions, chartOptions);

      this.chart = new window.Chart(ctx, {
        type: chartType,
        data: chartData,
        options: finalOptions
      });
    } catch (error) {
      console.error('[ChartJS Hook] erro ao inicializar gráfico', error);
      this.showError();
    }
  },

  showError() {
    if (this.errorShown) return;

    const errorHost =
      this.el.querySelector('[data-role="chart-hook-error"]') ||
      this.el.parentElement?.querySelector('[data-role="chart-hook-error"]');

    if (errorHost) {
      errorHost.classList.remove('hidden');
      this._errorHost = errorHost;
      this.errorShown = true;
      return;
    }

    const fallbackErrorHost = document.createElement('div');
    const errorMessage = document.createElement('div');

    fallbackErrorHost.className = 'absolute inset-0 flex items-center justify-center bg-red-50/90 border border-red-200 rounded-lg';
    errorMessage.className = 'text-center text-red-600 text-sm font-medium';
    errorMessage.textContent = 'Não foi possível renderizar o gráfico';

    fallbackErrorHost.appendChild(errorMessage);
    this.el.appendChild(fallbackErrorHost);
    this._errorHost = fallbackErrorHost;
    this.errorShown = true;
  },

  hideError() {
    if (this._errorHost) {
      this._errorHost.classList.add('hidden');
    }
    this.errorShown = false;
  }
};

export const HelpPanel = {
  mounted() {
    try {
      const tenantId = this.el?.dataset?.tenantId || 'tenant';
      const clientId = this.el?.dataset?.clientId || '0';
      this._key = `help:v1:${tenantId}:${clientId}`;
      this._overlay = this.el.querySelector('#help-overlay');
      this._openers = document.querySelectorAll('[data-help-open]');
      this._closers = this.el.querySelectorAll('[data-help-close]');
      this._search = this.el.querySelector('#help-search');

      this._open = () => {
        if (!this._overlay) return;
        this._overlay.classList.remove('hidden');
        try { localStorage.setItem(this._key, 'open'); } catch (_) { }
        if (this._search) setTimeout(() => this._search.focus(), 10);
      };

      this._close = () => {
        if (!this._overlay) return;
        this._overlay.classList.add('hidden');
        try { localStorage.setItem(this._key, 'closed'); } catch (_) { }
      };

      this._onKey = (e) => {
        if (e.key === 'Escape') this._close();
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
          e.preventDefault();
          this._open();
        }
      };

      this._openers.forEach((btn) => btn.addEventListener('click', this._open, { passive: true }));
      this._closers.forEach((btn) => btn.addEventListener('click', this._close, { passive: true }));
      this._starters = this.el.querySelectorAll('[data-help-start]');
      this._onStartClick = (e) => {
        const topic = e.currentTarget?.dataset?.helpStart || 'generic';
        try {
          window.dispatchEvent(new CustomEvent('mealflow:help:start', { detail: { topic } }));
        } catch (_) { }
      };
      this._starters.forEach((btn) => btn.addEventListener('click', this._onStartClick, { passive: true }));
      window.addEventListener('keydown', this._onKey, { passive: false });

      const params = new URLSearchParams(window.location.search || '');
      if (params.get('help')) this._open();
      else {
        try {
          if (localStorage.getItem(this._key) === 'open') this._open();
        } catch (_) { }
      }

      this.handleEvent && this.handleEvent('open_help', () => this._open());
    } catch (e) {
      console.warn('HelpPanel init failed:', e);
    }
  },

  destroyed() {
    try {
      if (this._openers) this._openers.forEach((btn) => btn.removeEventListener('click', this._open));
      if (this._closers) this._closers.forEach((btn) => btn.removeEventListener('click', this._close));
      if (this._starters) this._starters.forEach((btn) => btn.removeEventListener('click', this._onStartClick));
      if (this._onKey) window.removeEventListener('keydown', this._onKey);
    } catch (_) { }
  }
};

export const ReservationsPrint = {
  mounted() {
    this._onClick = (event) => {
      event.preventDefault();
      try {
        this._printListing();
      } catch (error) {
        console.warn('ReservationsPrint failed:', error);
        window.print();
      }
    };

    this.el.addEventListener('click', this._onClick, { passive: false });
  },

  destroyed() {
    try {
      this.el.removeEventListener('click', this._onClick);
    } catch (_) { }
  },

  _printListing() {
    const headerEl = document.getElementById('slot-print-header');
    const listRows = Array.from(document.querySelectorAll('#slot-print-root .slot-row'));

    if (!listRows.length) {
      window.print();
      return;
    }

    const getText = (element, selector) => (element.querySelector(selector)?.textContent || '').trim();
    const includeDessertColumn = listRows.some((row) => row.querySelector('.col-dessert'));
    const gridColumns = includeDessertColumn ? '40% 44% 16%' : '45% 55%';
    const columnAlignmentRule = includeDessertColumn ? '.c1, .c2, .c3' : '.c1, .c2';
    const tableHeaderHTML = includeDessertColumn
      ? '<div class="slot-table-header"><div>Nome</div><div>Refeição</div><div>Sobremesa</div></div>'
      : '<div class="slot-table-header"><div>Nome</div><div>Refeição</div></div>';

    const rowsHTML = listRows
      .map((row) => {
        const consumer =
          getText(row, '.col-consumer .consumer-print-name') ||
          getText(row, '.col-consumer .consumer-text') ||
          getText(row, '.col-consumer');
        const consumerCode = getText(row, '.col-consumer .consumer-id-badge');
        const mealSummary =
          getText(row, '.col-print-meal-summary') ||
          [getText(row, '.print-type'), getText(row, '.meals-text-print')]
            .filter(Boolean)
            .join(' | ');
        const dessert = getText(row, '.col-dessert') || '';
        const consumerBadge = consumerCode
          ? `<span class="consumer-id-badge">${escapeHtml(consumerCode)}</span>`
          : '';
        const consumerCell = `<div class="col c1"><span class="consumer-print-icon">👤</span>${consumerBadge}${escapeHtml(consumer)}</div>`;

        if (includeDessertColumn) {
          return `<div class="slot-row">${consumerCell}<div class="col c2">${escapeHtml(mealSummary)}</div><div class="col c3">${escapeHtml(dessert)}</div></div>`;
        }

        return `<div class="slot-row">${consumerCell}<div class="col c2">${escapeHtml(mealSummary)}</div></div>`;
      })
      .join('');

    const headerHTML = headerEl ? headerEl.innerHTML : '';

    const popupHTML = `<!doctype html>
    <html lang="pt-PT"><head><meta charset="utf-8"><title>Listagem de Reservas</title>
    <style>
      @page { size: A4 portrait; margin: 25mm; margin: 22%; }
      html, body { margin: 0; padding: 0; }
      body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 10px; color: #111827; }
      #root { padding: 12mm 10mm; }
      #slot-print-header { display: block; }
      #slot-print-header .grid { display: grid; grid-template-columns: auto 1fr auto; align-items: center; column-gap: 8px; }
      #slot-print-header img { height: 41px !important; max-height: 41px !important; width: auto !important; }
      #slot-print-header .text-center { overflow: hidden; }
      #slot-print-header .text-center > div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      #slot-print-header .date-line { font-size: 18px; font-weight: 700; text-align: center; }
      #slot-print-header .period-line { font-size: 16px; font-weight: 700; text-align: center; }
      #slot-print-header .print-title { font-size: 18px; font-weight: 700; text-align: center; margin-top: 4mm; }
      #slot-print-header .type-counters { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 10px 16px; justify-content: center; }
      #slot-print-header .type-counter { display: inline-flex; align-items: center; border: 1px solid #cbd5e1; border-radius: 3px; padding: 4px 9px; font-size: 13px; line-height: 1.15; }
      #slot-print-header .type-counter .emoji { margin-right: 8px; }
      #slot-print-header .type-counter .count { border-left: 1px solid #cbd5e1; margin-left: 9px; padding-left: 9px; font-weight: 700; }
      .list { border: 1px solid #d1d5db; }
      .slot-table-header { display: grid; grid-template-columns: ${gridColumns}; align-items: center; column-gap: 6px; padding: 3px 0; border-bottom: 1px solid #cbd5e1; background: #f8fafc; color: #334155; font-size: 10px; line-height: 1.2; font-weight: 700; }
      .slot-row { display: grid; grid-template-columns: ${gridColumns}; align-items: center; column-gap: 6px; padding: 2px 0; border-bottom: 1px solid #e5e7eb; }
      .slot-row:last-child { border-bottom: 0; }
      .slot-row .c1 { font-weight: 700; }
      .slot-row .consumer-print-icon { margin-right: 4px; font-size: 10px; font-weight: 400; }
      .slot-row .consumer-id-badge { display: inline-block; margin-right: 5px; padding: 1px 4px; border: 1px solid #cbd5e1; border-radius: 3px; background: #f8fafc; color: #334155; font-size: 9px; font-weight: 700; }
      ${columnAlignmentRule} { text-align: left; }
    </style></head>
    <body><div id="root">
      <div id="slot-print-header">${headerHTML}</div>
      <div class="list">${tableHeaderHTML}${rowsHTML}</div>
    </div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); window.close(); }, 200); };</script>
    </body></html>`;

    const popup = window.open('', '_blank', 'width=1000,height=700,scrollbars=yes');

    if (!popup) {
      window.print();
      return;
    }

    popup.document.open('text/html');
    popup.document.write(popupHTML);
    popup.document.close();
  }
};

export const HelpTour = {
  mounted() {
    try {
      this._active = false;
      this._idx = 0;
      this._steps = [];
      this._overlay = null;
      this._onKey = (e) => {
        if (!this._active) return;
        if (e.key === 'Escape') return this._end();
        if (e.key === 'ArrowRight' || e.key.toLowerCase() === 'n') return this._next();
        if (e.key === 'ArrowLeft' || e.key.toLowerCase() === 'p') return this._prev();
      };
      window.addEventListener('keydown', this._onKey, { passive: true });

      const start = (topic) => {
        this._collectSteps(topic);
        if (!this._steps.length) {
          console.warn('Sem etapas para o tour. Adicione atributos data-help="...".');
          return;
        }
        this._buildOverlay();
        this._active = true;
        this._idx = 0;
        this._show();
      };

      this._listener = (e) => start((e?.detail && e.detail.topic) || 'generic');
      window.addEventListener('mealflow:help:start', this._listener);

      this.handleEvent && this.handleEvent('start_help_tour', ({ topic }) => start(topic || 'generic'));
    } catch (e) {
      console.warn('HelpTour init failed:', e);
    }
  },

  destroyed() {
    try {
      if (this._listener) window.removeEventListener('mealflow:help:start', this._listener);
      if (this._onKey) window.removeEventListener('keydown', this._onKey);
      this._teardownOverlay();
    } catch (_) { }
  },

  _collectSteps(topic) {
    const selector =
      topic && topic !== 'generic'
        ? `[data-help][data-help-topic="${topic}"]`
        : '[data-help]';

    const elements = Array.from(document.querySelectorAll(selector));
    this._steps = elements.map((el, i) => ({
      el,
      title: el.dataset.helpTitle || `Passo ${i + 1}`,
      desc: el.dataset.help || ''
    }));

    if (!this._steps.length) {
      const helpButton = document.querySelector('[data-help-open]');
      if (helpButton) {
        this._steps = [{ el: helpButton, title: 'Ajuda', desc: 'Use este botão para abrir a ajuda.' }];
      }
    }
  },

  _buildOverlay() {
    const overlay = document.getElementById('help-tour-overlay');
    if (!overlay) {
      this._overlay = null;
      return;
    }

    if (!this._handleCloseClick) {
      this._handleCloseClick = () => this._end();
    }

    this._teardownOverlay();

    overlay.classList.remove('hidden');
    overlay.querySelector('[data-role="close"]')?.addEventListener('click', this._handleCloseClick);
    overlay.querySelector('[data-role="scrim"]')?.addEventListener('click', this._handleCloseClick);
    this._overlay = overlay;
  },

  _show() {
    const step = this._steps[this._idx];
    if (!step) return this._end();

    try {
      const rect = step.el.getBoundingClientRect();
      const ring = this._overlay.querySelector('[data-role="ring"]');
      const pop = this._overlay.querySelector('[data-role="popover"]');
      const title = this._overlay.querySelector('[data-role="title"]');
      const desc = this._overlay.querySelector('[data-role="desc"]');
      const prevButton = this._overlay.querySelector('[data-action="prev"]');
      const nextButton = this._overlay.querySelector('[data-action="next"]');

      if (!ring || !pop || !title || !desc || !prevButton || !nextButton) {
        return this._end();
      }

      ring.style.left = `${Math.max(8, rect.left - 8)}px`;
      ring.style.top = `${Math.max(8, rect.top - 8)}px`;
      ring.style.width = `${rect.width + 16}px`;
      ring.style.height = `${rect.height + 16}px`;

      title.textContent = step.title;
      desc.textContent = step.desc || ' ';

      const placeRight = rect.right + 360 < window.innerWidth;
      pop.style.left = placeRight ? `${rect.right + 16}px` : `${Math.max(16, rect.left - 340)}px`;
      pop.style.top = `${Math.max(16, rect.top)}px`;

      prevButton.onclick = () => this._prev();
      nextButton.onclick = () => this._next();

      step.el.scrollIntoView && step.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      this._end();
    }
  },

  _next() {
    if (this._idx < this._steps.length - 1) {
      this._idx += 1;
      this._show();
    } else {
      this._end();
    }
  },

  _prev() {
    if (this._idx > 0) {
      this._idx -= 1;
      this._show();
    }
  },

  _end() {
    this._active = false;
    this._teardownOverlay();
  },

  _teardownOverlay() {
    const overlay = this._overlay || document.getElementById('help-tour-overlay');
    if (!overlay) return;

    if (!this._handleCloseClick) {
      this._handleCloseClick = () => this._end();
    }

    overlay.querySelector('[data-role="close"]')?.removeEventListener('click', this._handleCloseClick);
    overlay.querySelector('[data-role="scrim"]')?.removeEventListener('click', this._handleCloseClick);
    overlay.classList.add('hidden');
    this._overlay = null;
  }
};

export const RfidCapture = {
  mounted() {
    try {
      window.__kioskRFIDWedge?.detach?.();
    } catch (_) { }

    this._buf = '';
    this._lastTs = 0;
    this._timer = null;
    this._maxGap = 60;
    this._minLen = 4;
    this._timeoutMs =
      parseInt(this.el?.dataset?.timeoutMs || this.el?.getAttribute('data-timeout-ms') || '15000', 10) || 15000;
    this._captureRef = this.el?.dataset?.captureRef;

    try {
      const bar = this.el.querySelector('#rfid-progress');
      if (bar) {
        const start = performance.now();
        const step = () => {
          const elapsed = performance.now() - start;
          const pct = Math.max(0, Math.min(100, (elapsed / this._timeoutMs) * 100));
          bar.style.width = `${pct}%`;
          if (elapsed < this._timeoutMs) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    } catch (_) { }

    this._timeoutHandle = setTimeout(() => {
      try {
        this.pushEvent('rfid_capture_timeout', { capture_ref: this._captureRef });
      } catch (_) { }
    }, this._timeoutMs + 100);

    this._onKey = (e) => {
      const tag = (document.activeElement && document.activeElement.tagName) || '';
      if (/(INPUT|TEXTAREA|SELECT)/.test(tag)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      const now = performance.now();
      const ch = e.key;
      if (ch === 'Enter' || ch === 'Tab') {
        if (this._buf.length >= this._minLen) {
          const raw = this._buf;
          this._buf = '';
          this._finalize(raw);
          return;
        }
        this._buf = '';
        return;
      }

      const ok = /^[0-9A-Za-z:;\-]$/.test(ch);
      if (!ok) {
        this._buf = '';
        return;
      }

      const gap = now - (this._lastTs || now);
      this._lastTs = now;
      if (gap > this._maxGap) this._buf = '';
      this._buf += ch;

      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        if (this._buf.length >= this._minLen) {
          const raw = this._buf;
          this._buf = '';
          this._finalize(raw);
        } else {
          this._buf = '';
        }
      }, 120);
    };

    window.addEventListener('keydown', this._onKey, true);
  },

  destroyed() {
    try {
      window.removeEventListener('keydown', this._onKey, true);
    } catch (_) { }
    clearTimeout(this._timer);
    clearTimeout(this._timeoutHandle);

    try {
      if (typeof window.__kioskRFIDReinit === 'function') {
        window.__kioskRFIDReinit();
      }
    } catch (_) { }
  },

  _finalize(raw) {
    try {
      const normalized = (raw || '').toString().trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
      if (!normalized) return;
      this.pushEvent('rfid_card_captured', {
        card_number: normalized,
        capture_ref: this.el?.dataset?.captureRef
      });
    } catch (_) { }
  }
};

export const AdvancedVisualRuntimeHooks = {
  ChartJSHook,
  HelpPanel,
  ReservationsPrint,
  HelpTour,
  RfidCapture
};
